import {  } from '@reduxjs/toolkit';
import { getSeedsForGrower } from '../Seeds/SeedsActions';
import { clearSeeds } from '../Seeds/SeedsSlice';
import { AppDispatch, RootState } from '../Store';
import { setSelectedGrowerId, clearSelectedPlanIdState, setEnogenFlowDisplayed } from './UISlice';
import { clearState as ClearFieldPlanState } from '../Plans/FieldPlan/FieldPlanSlice';
import { createTracedAsyncThunk } from '../../../tracing/trace';
import { getAllPlantingRates } from '../Seeds/PlantingRateSlice';
import { clearProductGamePlanV7State } from '../Plans/ProductGamePlanSliceV7';
import { Api } from '../../Api/Api';

interface IDisplayEnogenFlowArgs
{
	selectedGrowerId: string;
	apiBaseUrl: string;
	enogenFlowBaseUrl: string;
	userId: string;
	contractId?: string;
	viewDetails?: boolean;
	assignFields?: boolean;
}

/**
 * Opens the Enogen / map-centric iframe by resolving an access token and building the URL.
 *
 * Legacy (password / SSO) sessions carry a bearer token in `auth.userAuthToken`, which we use
 * directly. Okta sessions authenticate via an HttpOnly cookie and have no client-side token, so
 * we fetch one on demand from the cookie-authenticated `authentication/access-token` endpoint.
 * Since the iframe lives on a different domain, the cookie can't travel with it -- the token must
 * be embedded in the URL.
 */
export const displayEnogenFlow = createTracedAsyncThunk<void, IDisplayEnogenFlowArgs, { dispatch: AppDispatch; state: RootState; }>(
	'ui/displayEnogenFlow',
	async (context, args, thunkAPI) =>
	{
		let accessToken = thunkAPI.getState().auth.userAuthToken;

		if (!accessToken)
		{
			// Okta cookie session: mint a token from the current session (credentials: 'include').
			const api = new Api('/api/6', undefined, context);
			const response = await api.getAsync<string>('authentication/access-token');

			if (response.ErrorCode !== null || !response.Success || !response.Data)
			{
				return thunkAPI.rejectWithValue(response.ErrorMessage ?? 'Could not obtain an access token for the Enogen flow.');
			}

			accessToken = response.Data;
		}

		thunkAPI.dispatch(setEnogenFlowDisplayed({ ...args, accessToken }));
	}
);

/**
 * Clears the seeds and their rankings from a previous grower on selecting a new grower, and pulls the newly selected grower's seeds 
 */
export const selectGrowerAndPullSeeds = createTracedAsyncThunk<null, string, { dispatch: AppDispatch; state: RootState; }>(
	'selectGrower/pullSeeds',
	async (context, request, thunkAPI) =>
	{
		try
		{
			// Set the Grower ID
			thunkAPI.dispatch(setSelectedGrowerId(request));

			// Clear the current seeds
			thunkAPI.dispatch(clearSeeds());

			// Get the seeds list and planting rates for the selected grower
			getSeedsAndPlantingRatesForGrower(thunkAPI.dispatch, thunkAPI.getState);

			// Clear any plan data
			thunkAPI.dispatch(ClearFieldPlanState());
			thunkAPI.dispatch(clearSelectedPlanIdState());
			thunkAPI.dispatch(clearProductGamePlanV7State());

			return null;
		}
		catch (e)
		{
			return thunkAPI.rejectWithValue(e);
		}
	}
);

/**
 * These two operations need to be sequenced one after the other so they have their own function. 
 */
async function getSeedsAndPlantingRatesForGrower(dispatch: AppDispatch, getState: () => RootState)
{
	await dispatch(getSeedsForGrower());

	const state = getState();
	if (!state.seeds.series)
		return;

	await getAllPlantingRates(dispatch, state.seeds.series);
}